import { FC, useMemo, useState } from 'react';

import { DownloadIcon } from '@heroicons/react/outline';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';

import { formatDate } from '../../utils/delivery.utils';
import { formatWon } from '../../utils/settlement.utils';
import { computeRebatePayableFromLines } from '../../utils/rebate-table.utils';
import { exportRebateYearSummaryExcel } from '../../utils/rebateSettlementExcel';
import {
  getKstYear,
  MONTHS,
  statusColor,
  statusLabel,
} from '../../utils/rebate-settlement.utils';
import { RebateHistoryDetailModal } from './RebateHistoryDetailModal';

/**
 * 완료 내역 테이블에 표기할 정산 금액.
 * - Paid(완료): 이미 지급된 동결 금액 `totalAmount` 그대로
 * - Draft(예정): 정산 예정 테이블과 동일 공식으로 재계산 (두 화면 금액 일치)
 */
const settlementDisplayAmount = (s: GiftRebateSettlement): number =>
  s.status === GiftSettlementStatus.Paid
    ? s.totalAmount
    : computeRebatePayableFromLines(s.lines);

// ─────────────────────────────────────────────
// RebateHistoryYearCalendar
// ─────────────────────────────────────────────

/** `RebateHistoryYearCalendar` 컴포넌트의 props */
export interface RebateHistoryYearCalendarProps {
  /** 리베이트 정산 완료 내역 목록 (undefined이면 로딩 전 상태) */
  historyItems: GiftRebateSettlement[] | undefined;
  /** 데이터 로딩 중 여부 */
  loading: boolean;
}

/**
 * 리베이트(상조 전용) 정산 완료 내역을 연도별 12개월 캘린더 형태로 렌더링하는 컴포넌트.
 *
 * - 연도 선택기(select)로 조회 연도를 전환할 수 있다.
 * - 정산 데이터가 있는 월은 클릭 시 라인별 상세(상품/수익금/수량/수익금 합계)를 펼쳐 보여준다.
 * - 정산 데이터가 없는 월은 '미정' 상태로 빈 행을 표시한다.
 * - 하단 합계 행에 선택 연도의 총 건수와 총 수익금을 표시한다.
 */
export const RebateHistoryYearCalendar: FC<RebateHistoryYearCalendarProps> = ({
  historyItems,
  loading,
}) => {
  const currentYear = useMemo(() => getKstYear(), []);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  // 상세보기 모달에 띄울 정산 문서 (null이면 닫힘)
  const [detailSettlement, setDetailSettlement] = useState<GiftRebateSettlement | null>(
    null,
  );

  const yearOptions = useMemo(() => {
    const years = new Set<number>([currentYear]);
    (historyItems ?? []).forEach((s) => years.add(s.year));
    return Array.from(years).sort((a, b) => b - a);
  }, [historyItems, currentYear]);

  const monthMap = useMemo(() => {
    const map = new Map<number, GiftRebateSettlement>();
    (historyItems ?? []).forEach((s) => {
      if (s.year === selectedYear) map.set(s.month, s);
    });
    return map;
  }, [historyItems, selectedYear]);

  const yearTotalCount = useMemo(
    () => Array.from(monthMap.values()).reduce((sum, s) => sum + s.lines.length, 0),
    [monthMap],
  );
  const yearTotalAmount = useMemo(
    () =>
      Array.from(monthMap.values()).reduce(
        (sum, s) => sum + settlementDisplayAmount(s),
        0,
      ),
    [monthMap],
  );

  return (
    <div>
      {/* 연도 선택 */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">연도</span>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="rounded border border-gray-300 px-2 py-1"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}년
            </option>
          ))}
        </select>
        <button
          onClick={() =>
            exportRebateYearSummaryExcel(selectedYear, historyItems ?? [])
          }
          disabled={monthMap.size === 0}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <DownloadIcon className="h-4 w-4" />
          엑셀 다운로드
        </button>
      </div>

      {loading ? (
        <div className="center-box py-8">
          <Loading />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">정산월</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">지급일</th>
                <th className="px-3 py-2 text-right">건수</th>
                <th className="px-3 py-2 text-right">총 수익금 (VAT 포함)</th>
                <th className="px-3 py-2 text-center">상세내역</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {MONTHS.map((m) => {
                const s = monthMap.get(m);

                if (!s) {
                  return (
                    <tr key={m} className="text-gray-400">
                      <td className="whitespace-nowrap px-3 py-3 font-medium">
                        {selectedYear}년 {m}월
                      </td>
                      <td className="px-3 py-3">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                          미정
                        </span>
                      </td>
                      <td className="px-3 py-3">-</td>
                      <td className="px-3 py-3 text-right">-</td>
                      <td className="px-3 py-3 text-right">-</td>
                      <td className="px-3 py-3 text-center">-</td>
                    </tr>
                  );
                }

                return (
                  <tr key={s._id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-3 font-medium">
                      {s.year}년 {s.month}월
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(s.status)}`}
                      >
                        {statusLabel(s.status)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                      {s.executedAt ? (
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(s.status)}`}
                          >
                            완료
                          </span>
                          {formatDate(s.executedAt)}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(s.status)}`}
                          >
                            예정
                          </span>
                          {/* 예정 지급일: max month+1 (12월이면 다음해 1월) 15일 — 완료와 동일하게 yy.mm.dd */}
                          {formatDate(
                            new Date(
                              s.month === 12 ? s.year + 1 : s.year,
                              s.month === 12 ? 0 : s.month,
                              15,
                            ),
                          )}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                      {s.lines.length}건
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-gray-800">
                      {formatWon(settlementDisplayAmount(s))}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => setDetailSettlement(s)}
                        className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                      >
                        상세보기
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 text-sm font-semibold">
              <tr>
                <td className="px-3 py-3 text-gray-500">{selectedYear}년 합계</td>
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {yearTotalCount}건
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
                  {formatWon(yearTotalAmount)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* 상세보기 모달 */}
      {detailSettlement && (
        <RebateHistoryDetailModal
          settlement={detailSettlement}
          onClose={() => setDetailSettlement(null)}
        />
      )}
    </div>
  );
};
